import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GeneralizedTableRow from '../common/table_row';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import { useNavigate, useLocation } from 'react-router-dom';
import routes from '../../routes';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import GeneralButton from '../common/button_general';
import TableHeader from '../common/table_header';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import CustomerFilterComponent from '../common/filtering/customer_filter_list';

function CustomerList() {
    const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting("asc_last_name");

    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchQueryParam = queryParams.get('search');
    const clientQueryParam = queryParams.get('client');
    const statusQueryParam = queryParams.get('status');
    const deliveryDayQueryParam = queryParams.get('delivery_day');
    const providerRouteQueryParam = queryParams.get('provider_route');

    const currentRoute = routes.find(route => route.path === location.pathname);
    const routePageTitle = currentRoute ? currentRoute.pageTitle : '';
    const [searchQuery] = useState(searchQueryParam || '');
    const [client, setClient] = useState(clientQueryParam || '');
    const [status, setStatus] = useState(statusQueryParam || '');
    const [deliveryDay, setDeliveryDay] = useState(deliveryDayQueryParam || '');
    const [providerRoute, setProviderRoute] = useState(providerRouteQueryParam || '');

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const getDayName = (dayValue) => {
        const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        return days[dayValue] || '';
    };

    useEffect(() => {
        const fetchCustomers = async () => {
            if (!pageSize) return;
            setIsLoading(true);
            try {
                const apiToken = localStorage.getItem('accessToken');
                const apiUrl = process.env.REACT_APP_API_URL;

                const response = await axios.get(`${apiUrl}/customers`, {
                    headers: getHeaders(apiToken),
                    params: { 
                        page, 
                        pageSize, 
                        ...(sortBy && { order_by: sortBy }), 
                        ...(searchQuery && { search: searchQuery }),
                        ...(client && { client_id: client }), 
                        ...(status && { status: status }),
                        ...(deliveryDay && { delivery_day: deliveryDay }),
                        ...(providerRoute && { provider_route: providerRoute })
                    },
                });
                setTotalPages(response.data.totalPages);
                setTotalRecords(response.data.total);
                setCustomers(response.data.data);
            } catch (error) {
                handleErrorResponse(error, navigate, setShowError, setErrorMessage);
            }
            finally {
                setIsLoading(false);
            }
        };

        fetchCustomers();
    }, [page, pageSize, sortBy, searchQuery, client, status, deliveryDay, providerRoute]);

    const handleDelete = async (customer) => {
        const confirmed = window.confirm(`Are you sure you want to delete customer ${customer.first_name} ${customer.last_name}?`);
        if (!confirmed) return;
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const apiToken = localStorage.getItem('accessToken');
            await axios.delete(`${apiUrl}/customers/${customer.customer_id}`, {
                headers: getHeaders(apiToken)
            });
            setCustomers(prev => prev.filter(c => c.customer_id !== customer.customer_id));
        } catch (error) {
            handleErrorResponse(error, navigate, setShowError, setErrorMessage);
        }
    };

    const handleExport = async () => {
        setIsLoading(true);
        try {
            const apiToken = localStorage.getItem('accessToken');
            const apiUrl = process.env.REACT_APP_API_URL;

            // Fetch all customers by making multiple paginated requests
            let allCustomers = [];
            let currentPage = 1;
            let totalPages = 1;
            const maxPageSize = 1000; // API maximum

            do {
                const response = await axios.get(`${apiUrl}/customers`, {
                    headers: getHeaders(apiToken),
                    params: {
                        page: currentPage,
                        pageSize: maxPageSize,
                        ...(sortBy && { order_by: sortBy }),
                        ...(searchQuery && { search: searchQuery }),
                        ...(client && { client_id: client }),
                        ...(status && { status: status }),
                        ...(deliveryDay && { delivery_day: deliveryDay }),
                        ...(providerRoute && { provider_route: providerRoute })
                    },
                });

                allCustomers = allCustomers.concat(response.data.data);
                totalPages = response.data.totalPages;
                currentPage++;
            } while (currentPage <= totalPages);

            const exportData = allCustomers;

            // Helper function to escape CSV values
            const escapeCsvValue = (value) => {
                if (value === null || value === undefined) return '';
                const stringValue = String(value);
                // If value contains comma, quote, or newline, wrap in quotes and escape quotes
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            };

            // Create CSV content
            const headers = ['customer_id', 'last_name', 'first_name', 'middle_name', 'suffix', 'client_name', 'status', 'delivery_day', 'provider_route', 'client_number'];
            const csvContent = [
                headers.join(','),
                ...exportData.map(customer => [
                    escapeCsvValue(customer.customer_id),
                    escapeCsvValue(customer.last_name),
                    escapeCsvValue(customer.first_name),
                    escapeCsvValue(customer.middle_name),
                    escapeCsvValue(customer.suffix),
                    escapeCsvValue(customer.client_name),
                    escapeCsvValue(customer.status),
                    escapeCsvValue(getDayName(customer.delivery_days)),
                    escapeCsvValue(customer.internal_route),
                    escapeCsvValue(customer.client_number)
                ].join(','))
            ].join('\n');

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `clients_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            handleErrorResponse(error, navigate, setShowError, setErrorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
        {
            id: 'customer_full_name',
            name: 'Name',
            textColor: 'text-gray-900',
            format: (customer) => {
                let name = customer.last_name;
                name += ', ' + customer.first_name;
                if (customer.middle_name && customer.middle_name.trim()) {
                    name += ' ' + customer.middle_name.charAt(0) + '.';
                }
                if (customer.suffix && customer.suffix.trim()) {
                    name += ', ' + customer.suffix;
                }
                return name;
            },
            link: (value) => `/clients/${value.customer_id}`,
            target: 'edit_client',            
            sortColumn: 'last_name'
        },
        { 
            id: 'client_name', name: 'Provider', textColor: 'text-gray-500'
        },
        { 
            id: 'status', name: 'Status', 
            format: value => value ? value.charAt(0).toUpperCase() + value.slice(1) : '' 
        },
        {
            id: 'delivery_day', name: 'Delivery Day',
            format: (customer) => getDayName(customer.delivery_days)
        },
        {
            id: 'internal_route', name: 'Provider Route'
        },
        {
            id: 'last_order_date',
            name: 'Last Order Date',
            format: (cellValue, row) => {
                const dateStr = row && row.last_order_date ? row.last_order_date : cellValue;
                return dateStr ? new Date(dateStr).toLocaleDateString() : 'N/A';
            },
            link: (row) => row.last_order_id ? `/orders/${row.last_order_id}` : null
        },
        {
            id: 'edit',
            name: '',
            link: (rowData) => `/clients/${rowData.customer_id}`,
            tooltip: 'Edit Client',
            icon: PencilSquareIcon
        },
        {
            id: 'delete',
            name: '',
            tooltip: 'Delete Customer',
            render: (rowData) => (
                <button onClick={() => handleDelete(rowData)} title="Delete Customer">
                    <TrashIcon className="h-5 w-5 text-red-500" />
                </button>
            )
        }
    ];

    const pageTitle = routePageTitle ? routePageTitle : 'Clients';

    return (
        <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
            {showError && (
                <ErrorMessage
                    message={errorMessage}
                    linkText=""
                    linkHref="/retry-url"
                />
            )}
            <div className="px-2 sm:px-3 lg:px-3">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
                    <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
                    <div className="flex gap-2">
                        <GeneralButton type="button" buttonType="secondary-sm" onClick={handleExport}>
                            Export Clients
                        </GeneralButton>
                        <GeneralButton type="button" buttonType="secondary-sm" onClick={() => navigate('/clients/import')}>
                            Import Clients
                        </GeneralButton>
                        <GeneralButton type="submit" buttonType="primary-sm" onClick={() => navigate('/clients/create')}>
                            Create Client
                        </GeneralButton>
                    </div>
                </div>
                <CustomerFilterComponent 
                    onFilterChange={(client, status, deliveryDay, providerRoute) => {
                        setClient(client ? client.client_id : '');
                        setStatus(status);
                        setDeliveryDay(deliveryDay);
                        setProviderRoute(providerRoute);
                    }} 
                    selectedClient={client}
                    selectedStatus={status}
                    selectedDeliveryDay={deliveryDay}
                    selectedProviderRoute={providerRoute}
                />
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
                    <table className="min-w-full divide-y divide-gray-300">
                        <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">
                                        <Spinner minHeight={630} />
                                    </td>
                                </tr>
                            ) : (
                                customers.map((customer) => (
                                    <GeneralizedTableRow key={customer.customer_id} rowData={customer} columns={columns} />
                                ))
                            )}
                        </tbody>
                    </table>
                    {!isLoading && (
                        <PaginationControl
                            page={page}
                            recordsPerPage={pageSize}
                            totalRecords={totalRecords}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                        />
                    )}
                </div>
            </div>
        </MasterLayout>
    );
}

export default CustomerList;
